/**
 * ui/services/replays.ts
 *
 * Saves a replay for every completed puzzle (solo and multiplayer) and reads
 * them back for the replay page, rewatch buttons, and the history screen.
 */
import type { PuzzleDefinition } from '@nonogram/shared';
import type { MoveCommand } from '../../engine/history.js';
import type { ReplayPlayer, ReplayRecord, ReplayStorePort, ReplaySummary } from '../../ports/replays.js';
import { IndexedDBReplayStore } from '../../adapters/indexed-db-replays.js';
import { newId, ProfileService } from './profile.js';
import { recordDeletions } from './sync-deletions.js';

export class ReplayService {
  static readonly instance = new ReplayService(new IndexedDBReplayStore(), ProfileService.instance);

  private readonly _listeners = new Set<() => void>();

  constructor(
    private readonly _store: ReplayStorePort,
    private readonly _profile: Pick<ProfileService, 'get'>,
    private readonly _now: () => number = Date.now,
    private readonly _newId: () => string = newId,
  ) {}

  /**
   * Saves a completed game. Returns its id, or the existing replay's id when
   * the same game was already saved (reopening a solved puzzle solves it
   * "again" with the same log). Returns null for an empty log.
   */
  async record(options: {
    puzzle: Pick<PuzzleDefinition, 'id' | 'name' | 'rows' | 'columns' | 'colors' | 'givens'>;
    solveTimeMs: number;
    moveLog: MoveCommand[];
    mode: 'solo' | 'multiplayer';
    players?: ReplayPlayer[];
  }): Promise<string | null> {
    const { puzzle, solveTimeMs, moveLog, mode, players } = options;
    if (moveLog.length === 0) return null;
    const existing = (await this._store.listReplays()).find((replay) =>
      replay.puzzleId === puzzle.id
      && replay.mode === mode
      && replay.moveCount === moveLog.length
      // A reopened save's time can differ from the solve's by a few ms.
      && Math.abs(replay.solveTimeMs - solveTimeMs) < 1000);
    if (existing) return existing.id;

    const record: ReplayRecord = {
      id: this._newId(),
      puzzleId: puzzle.id,
      puzzleName: puzzle.name,
      rows: puzzle.rows,
      columns: puzzle.columns,
      colors: [...puzzle.colors],
      solveTimeMs,
      completedAt: this._now(),
      mode,
      profileId: this._profile.get().id,
      moveCount: moveLog.length,
      moveLog: moveLog.map((move) => ({ ...move })),
      ...(players && players.length > 0 ? { players } : {}),
      ...(puzzle.givens ? { givens: structuredClone(puzzle.givens) } : {}),
    };
    await this._store.saveReplay(record);
    this._notify();
    return record.id;
  }

  getReplay(id: string): Promise<ReplayRecord | null> {
    return this._store.getReplay(id);
  }

  listReplays(): Promise<ReplaySummary[]> {
    return this._store.listReplays();
  }

  /** The newest replay per puzzle id. */
  async latestByPuzzle(): Promise<Map<string, ReplaySummary>> {
    const latest = new Map<string, ReplaySummary>();
    for (const replay of await this._store.listReplays()) {
      if (!latest.has(replay.puzzleId)) latest.set(replay.puzzleId, replay);
    }
    return latest;
  }

  async deleteReplay(id: string): Promise<void> {
    await this._store.deleteReplay(id);
    recordDeletions('replay', [id]);
    this._notify();
  }

  async clearReplays(): Promise<void> {
    const ids = (await this._store.listReplays()).map((replay) => replay.id);
    await this._store.clearReplays();
    recordDeletions('replay', ids);
    this._notify();
  }

  /** Remembers the server id of a replay shared by link. */
  async markShared(id: string, sharedId: string): Promise<void> {
    const record = await this._store.getReplay(id);
    if (!record || record.sharedId === sharedId) return;
    await this._store.saveReplay({ ...record, sharedId });
    this._notify();
  }

  /** Replays changed without this service (account sync brought some in). */
  notifyChanged(): void {
    this._notify();
  }

  onChanged(listener: () => void): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  private _notify(): void {
    for (const listener of this._listeners) listener();
  }
}
